import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export function useRefOptions() {
  const ref = useRefData();
  const optionsFor = (source, labelKey) => {
    const list = ref?.[source] || [];
    return list.map((x) => ({ value: x.id, label: x[labelKey] || x.name || x.full_name }));
  };
  return { optionsFor, reload: ref?.reload };
}

export default function CrudPage({ title, subtitle, endpoint, columns, fields, testid, canManage = true, detailBase }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const { optionsFor, reload } = useRefOptions();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${endpoint}`);
      setItems(res.data);
    } catch (e) { toast.error("Failed to load data"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => { init[f.name] = f.default ?? (f.type === "number" ? 0 : ""); });
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (item) => {
    const init = {};
    fields.forEach((f) => { init[f.name] = item[f.name] ?? (f.type === "number" ? 0 : ""); });
    setForm(init); setEditing(item); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0; });
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      await load();
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${toDelete.id}`);
      toast.success("Deleted");
      setToDelete(null);
      await load();
      reload && reload();
    } catch (e) { toast.error("Delete failed"); }
  };

  const filtered = items.filter((it) =>
    !query || JSON.stringify(it).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={canManage && (
          <Button onClick={openCreate} data-testid={`${testid}-add-btn`} className="gap-2">
            <Plus size={16} strokeWidth={1.5} /> New
          </Button>
        )}
      />

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
          data-testid={`${testid}-search`}
        />
      </div>

      <Card className="overflow-hidden border-border">
        <Table data-testid={`${testid}-table`}>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              {columns.map((c) => (
                <TableHead key={c.key} className="text-xs uppercase tracking-wide font-semibold">{c.label}</TableHead>
              ))}
              {canManage && <TableHead className="text-right text-xs uppercase tracking-wide font-semibold">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-12 text-muted-foreground">No records found.</TableCell></TableRow>
            ) : filtered.map((item) => (
              <TableRow key={item.id} className={`hover:bg-muted/40 ${detailBase ? "cursor-pointer" : ""}`} onClick={detailBase ? () => navigate(`${detailBase}/${item.id}`) : undefined} data-testid={`${testid}-row`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className="py-3">
                    {c.render ? c.render(item)
                      : c.badge ? <StatusBadge value={item[c.key]} />
                      : c.date ? fmtDate(item[c.key])
                      : (item[c.key] ?? "—")}
                  </TableCell>
                ))}
                {canManage && (
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`${testid}-edit-btn`}>
                        <Pencil size={16} strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setToDelete(item)} data-testid={`${testid}-delete-btn`}>
                        <Trash2 size={16} strokeWidth={1.5} className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit ${title.slice(0, -1)}` : `New ${title.slice(0, -1)}`}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {fields.map((f) => (
              <div key={f.name} className="grid gap-1.5">
                <Label className="text-sm">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`${testid}-field-${f.name}`}
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={form[f.name] ? String(form[f.name]) : ""}
                    onValueChange={(v) => setForm({ ...form, [f.name]: v })}
                  >
                    <SelectTrigger data-testid={`${testid}-field-${f.name}`}>
                      <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || optionsFor(f.optionsFrom, f.labelKey || "name")).map((o) => (
                        <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`${testid}-field-${f.name}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel-btn`}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save-btn`}>{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid={`${testid}-delete-confirm`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
